/** Staff WebSocket for live ops screens. Writes stay on REST. */

export const STORE_QUEUE_CHANNEL = 'store:queue';

/** Thin events that mean the queue or summary is stale. */
export const QUEUE_REFETCH_TYPES = new Set([
  'order.placed',
  'order.status_changed',
  'order.assigned',
  'order.unassigned',
  'order.payment_updated',
]);

const PING_MS = 30 * 1000;
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30 * 1000;

const TERMINAL_CLOSE_CODES = new Set([1008, 4001, 4003, 4401, 4403]);

export function buildStoreSocketUrl(endpoint, accessToken) {
  const url = new URL(endpoint);
  // CRA expands `$default` in .env as an empty variable, which drops the stage.
  if (!url.pathname || url.pathname === '/') {
    url.pathname = '/$default';
  }
  url.searchParams.set('access_token', accessToken);
  return url.toString();
}

export function parseSocketMessage(raw) {
  if (raw == null || raw === '') return null;
  try {
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!data || typeof data !== 'object') return null;
    return data;
  } catch {
    return null;
  }
}

export function isForbiddenSocketError(message) {
  const code = String(message?.code || message?.error || '').toLowerCase();
  const text = `${code} ${message?.message || ''} ${message?.reason || ''}`.toLowerCase();
  return code === 'staff_required'
    || code === 'forbidden'
    || text.includes('staff_required')
    || text.includes('forbidden');
}

/**
 * Connect, subscribe to store:queue, and surface thin events.
 * Call `close()` when the live screen unmounts.
 */
export function connectStoreQueue({
  endpoint,
  accessToken,
  onEvent,
  onSocketError,
  WebSocketImpl = typeof WebSocket !== 'undefined' ? WebSocket : null,
} = {}) {
  if (!endpoint || !accessToken || !WebSocketImpl) {
    return { close() {} };
  }

  let socket = null;
  let closed = false;
  let retryTimer = null;
  let pingTimer = null;
  let attempt = 0;

  const clearRetry = () => {
    if (retryTimer != null) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
  };

  const clearPing = () => {
    if (pingTimer != null) {
      clearInterval(pingTimer);
      pingTimer = null;
    }
  };

  const send = (payload) => {
    if (!socket || socket.readyState !== WebSocketImpl.OPEN) return;
    socket.send(JSON.stringify(payload));
  };

  const scheduleReconnect = () => {
    if (closed) return;
    clearRetry();
    const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * (2 ** attempt));
    attempt += 1;
    retryTimer = setTimeout(open, delay);
  };

  function open() {
    if (closed) return;
    clearPing();
    let url;
    try {
      url = buildStoreSocketUrl(endpoint, accessToken);
    } catch {
      return;
    }
    socket = new WebSocketImpl(url);

    socket.onopen = () => {
      if (closed) return;
      attempt = 0;
      send({ action: 'subscribe', channel: STORE_QUEUE_CHANNEL });
      pingTimer = setInterval(() => send({ action: 'ping' }), PING_MS);
    };

    socket.onmessage = (event) => {
      const message = parseSocketMessage(event?.data);
      if (!message) return;
      if (message.type === 'pong') return;
      if (message.type === 'error') {
        onSocketError?.(message);
        if (isForbiddenSocketError(message)) {
          closed = true;
          clearRetry();
          clearPing();
          try { socket?.close(); } catch { /* already gone */ }
        }
        return;
      }
      if (QUEUE_REFETCH_TYPES.has(message.type)) onEvent?.(message);
    };

    socket.onclose = (event) => {
      clearPing();
      if (closed) return;
      if (TERMINAL_CLOSE_CODES.has(event?.code)) {
        closed = true;
        onSocketError?.({
          type: 'error',
          code: event?.code,
          reason: event?.reason || '',
        });
        return;
      }
      scheduleReconnect();
    };

    socket.onerror = () => {
      // The close handler decides whether to reconnect.
    };
  }

  open();

  return {
    close() {
      closed = true;
      clearRetry();
      clearPing();
      if (!socket) return;
      const current = socket;
      socket = null;
      try {
        if (current.readyState === WebSocketImpl.OPEN) {
          current.send(JSON.stringify({ action: 'unsubscribe', channel: STORE_QUEUE_CHANNEL }));
        }
      } catch {
        // Leaving the screen still drops the connection.
      }
      current.onclose = null;
      current.onmessage = null;
      current.onerror = null;
      current.onopen = null;
      try { current.close(); } catch { /* already closed */ }
    },
  };
}
